import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Film, Play, AlertCircle, Sparkles, Wallet, MessagesSquare } from 'lucide-react';
import { listStoryboards, type Storyboard } from '../../services/studioService';
import { useWallet } from '../../contexts/WalletContext';
import { STREAMS, type StreamDef } from '../studio/StreamPicker';
import { useStorageUrl } from './MemberAvatar';
import { TYPE } from '../../constants/typeScale';
import TrialNotice from '../studio/TrialNotice';

/**
 * StudioHome — the creator's launchpad, shown on /dashboard when no chat is open (replaces the bare "start a chat"
 * empty state). A LAUNCHPAD, not an ops/analytics console (see the A3 assessment): pick a work stream to start a
 * production, see your productions in flight + their render status, and your credits at a glance. All data already
 * exists as services; this is aggregation + layout. Monochrome, Lucide, mobile-first, four states.
 */
interface Props {
  /** Start a new production in a work stream — opens the Studio pre-seeded for that stream. */
  onStartProduction: (stream: StreamDef) => void;
  /** Open an existing production (storyboard) in the Studio. */
  onOpenProduction: (sb: Storyboard) => void;
  /** FR4: reach the SECONDARY chat list (mobile only — desktop already shows it as a sidebar). */
  onOpenChats?: () => void;
}

const STATUS_LABEL: Record<string, string> = { draft: 'Draft', generating: 'Making the film…', ready: 'Ready', failed: 'Didn’t come together' };

/** One production row — a resolved thumbnail (first preview frame) + title + status. */
const ProductionRow: React.FC<{ sb: Storyboard; onOpen: () => void }> = ({ sb, onOpen }) => {
  const thumbPath = sb.shots?.find((s) => s.previewFramePath)?.previewFramePath || null;
  const url = useStorageUrl(thumbPath);
  const failed = sb.status === 'failed';
  return (
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 rounded-xl border border-secondary-200 p-2.5 text-left hover:border-secondary-400 dark:border-secondary-600 dark:hover:border-secondary-600">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-secondary-100 dark:bg-secondary-800">
        {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <Film className="h-5 w-5 text-secondary-400" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block truncate ${TYPE.body} text-secondary-900 dark:text-white`}>{sb.title || 'Untitled episode'}</span>
        <span className={`${TYPE.meta} ${failed ? 'text-amber-600 dark:text-amber-500' : 'text-secondary-400'}`}>{STATUS_LABEL[sb.status] || 'Draft'}</span>
      </span>
      {sb.final_video_path && <Play className="h-4 w-4 shrink-0 text-secondary-400" />}
    </button>
  );
};

/** The one stream we currently sell. The rest stay reachable inside the Studio. */
const MICRO_DRAMA = STREAMS.find((s) => s.preset === 'micro_drama') || STREAMS[0];

const StudioHome: React.FC<Props> = ({ onStartProduction, onOpenProduction, onOpenChats }) => {
  const [productions, setProductions] = useState<Storyboard[] | null>(null);
  const [error, setError] = useState('');
  const { wp, plan, loading: walletLoading } = useWallet();

  useEffect(() => {
    let alive = true;
    listStoryboards()
      .then((r) => { if (alive) setProductions(r); })
      .catch(() => { if (alive) setError('Could not load your series.'); });
    return () => { alive = false; };
  }, []);

  const recent = (productions || []).slice(0, 5);
  const isFree = plan === 'free' || plan === 'FREE';

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {/* ONE way in (owner 2026-08-14). Four tiles asked a question whose answers had stopped differing —
            the Studio opens into micro-drama regardless — and "if it's the same, no point". A picker with one
            real answer is a step, not a choice. The other streams stay live inside the Studio, behind the
            stream chip's Change, which is where a capability we are not currently selling belongs. */}
        <h1 className={`${TYPE.stageTitle} text-secondary-900 dark:text-white`}>
          Make your next episode.
        </h1>
        <p className={`mt-1.5 ${TYPE.body} text-secondary-500 dark:text-secondary-400`}>
          Tell your crew the story. They draft it into shots, cast the faces, shoot it and cut it together.
        </p>

        <button
          type="button"
          data-testid="start-production"
          onClick={() => onStartProduction(MICRO_DRAMA)}
          className={`mt-5 inline-flex min-h-[44px] items-center gap-2.5 rounded-xl bg-secondary-900 px-5 ${TYPE.body}
                     font-semibold text-white transition-opacity hover:opacity-90 dark:bg-white dark:text-secondary-900`}
        >
          <MICRO_DRAMA.Icon className="h-4 w-4" />
          Start a series
        </button>
        <TrialNotice where="home" />

        {/* Your productions — in flight + recent, with render status */}
        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between">
            <h2 className={`${TYPE.control} font-semibold uppercase tracking-wide text-secondary-400`}>Your series</h2>
            <Link to="/wallet" className={`inline-flex items-center gap-1 ${TYPE.meta} text-secondary-400 hover:text-secondary-700 dark:hover:text-white`}>
              <Wallet className="h-3 w-3" /> {walletLoading ? '…' : wp.toLocaleString()} WP{isFree ? ' · Creator' : ''}
            </Link>
          </div>
          {error ? (
            <p className={`flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 ${TYPE.control} text-amber-700 dark:bg-amber-950/30 dark:text-amber-500`}><AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}</p>
          ) : productions === null ? (
            <div className={`flex items-center gap-2 py-6 ${TYPE.body} text-secondary-400`}><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : recent.length === 0 ? (
            <div className="rounded-xl border border-dashed border-secondary-200 py-6 text-center dark:border-secondary-600">
              <Sparkles className="mx-auto mb-2 h-5 w-5 text-secondary-300 dark:text-secondary-600" />
              <p className={`${TYPE.body} text-secondary-500 dark:text-secondary-400`}>Nothing yet. Start a series above and its episodes will live here.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {recent.map((sb) => <ProductionRow key={sb.id} sb={sb} onOpen={() => onOpenProduction(sb)} />)}
            </div>
          )}
        </div>

        {/* Chats — SECONDARY navigation (FR4). Mobile only: desktop already shows the list as a sidebar.
            Quiet by design; the launchpad's job is to start a production, not to route into a message stream. */}
        {onOpenChats && (
          <button type="button" onClick={onOpenChats}
            className={`mt-6 flex w-full items-center gap-2 ${TYPE.body} text-secondary-500 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-white md:hidden`}>
            <MessagesSquare className="h-4 w-4 shrink-0" /> Your crews
          </button>
        )}

        {isFree && (
          <Link to="/wallet" className={`mt-6 flex items-center justify-between rounded-2xl border border-secondary-200 p-3.5 ${TYPE.body} hover:border-secondary-400 dark:border-secondary-600 dark:hover:border-secondary-600`}>
            <span className="text-secondary-600 dark:text-secondary-300">Ready to charge for your episodes? Wisetree Plus lets you put a price on them.</span>
            <span className={`ml-3 shrink-0 rounded-lg bg-secondary-900 px-3 py-1.5 ${TYPE.control} font-semibold text-white dark:bg-white dark:text-secondary-900`}>See plans</span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default StudioHome;
